import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["input", "zone", "filename", "preview", "removeField", "removeLink", "hint"]
  static values = { summaryId: String, existingFilename: String }

  declare readonly inputTarget: HTMLInputElement
  declare readonly zoneTarget: HTMLElement
  declare readonly filenameTarget: HTMLElement
  declare readonly previewTarget: HTMLImageElement
  declare readonly removeFieldTarget: HTMLInputElement
  declare readonly hasRemoveFieldTarget: boolean
  declare readonly removeLinkTarget: HTMLElement
  declare readonly hasRemoveLinkTarget: boolean
  declare readonly hintTarget: HTMLElement
  declare readonly hasHintTarget: boolean
  declare readonly summaryIdValue: string
  declare readonly existingFilenameValue: string

  private previewUrl?: string
  private defaultHint = ""

  connect() {
    if (this.hasHintTarget) this.defaultHint = this.hintTarget.textContent || ""
    this.updateZoneState(Boolean(this.existingFilenameValue))
    this.updateSummaryText(this.existingFilenameValue || "未選択")
  }

  disconnect() {
    this.revokePreviewUrl()
  }

  dragenter(event: DragEvent) {
    event.preventDefault()
    this.zoneTarget.classList.add("border-primary")
  }

  dragover(event: DragEvent) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
  }

  dragleave(event: DragEvent) {
    if (event.relatedTarget instanceof Node && this.element.contains(event.relatedTarget)) return

    this.zoneTarget.classList.remove("border-primary")
  }

  drop(event: DragEvent) {
    event.preventDefault()
    this.zoneTarget.classList.remove("border-primary")
    const file = event.dataTransfer?.files[0]
    if (!file) return

    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.inputTarget.files = transfer.files
    this.renderFile(file)
  }

  preview() {
    const file = this.inputTarget.files?.[0]
    if (file) this.renderFile(file)
  }

  markForRemoval(event: Event) {
    event.preventDefault()
    this.inputTarget.value = ""
    // 新規登録フォームには削除フラグがない（消す対象は選択中のファイルだけ）
    if (this.hasRemoveFieldTarget) this.removeFieldTarget.value = "1"
    this.filenameTarget.textContent = "クリックまたはドラッグで画像をアップロード"
    this.previewTarget.classList.add("hidden")
    this.previewTarget.removeAttribute("src")
    if (this.hasRemoveLinkTarget) this.removeLinkTarget.classList.add("hidden")
    this.revokePreviewUrl()
    this.updateZoneState(false)
    this.updateSummaryText(this.hasRemoveFieldTarget ? "削除予定" : "未選択")
    this.updateSummaryPreview(undefined)
  }

  private renderFile(file: File) {
    if (this.hasRemoveFieldTarget) this.removeFieldTarget.value = "0"
    this.filenameTarget.textContent = `選択中： ${file.name}`
    this.revokePreviewUrl()
    this.previewUrl = URL.createObjectURL(file)
    this.previewTarget.src = this.previewUrl
    this.previewTarget.classList.remove("hidden")
    if (this.hasRemoveLinkTarget) this.removeLinkTarget.classList.remove("hidden")
    this.updateZoneState(true)
    this.updateSummaryText(file.name)
    this.updateSummaryPreview(this.previewUrl)
  }

  private updateZoneState(hasFile: boolean) {
    this.zoneTarget.classList.toggle("has-file", hasFile)
    if (!this.hasHintTarget) return

    this.hintTarget.textContent = hasFile ? "別の画像を選ぶには再度クリック" : this.defaultHint
  }

  // 確認ステップの表示更新。ファイル名は "<summaryId>"、サムネイルは "<summaryId>-preview" に書く。
  // どちらもこのコントローラの外にあるので、ターゲットではなく id で引く。
  //
  // テキストと画像を別メソッドにしているのは connect のため。編集画面の既存添付は
  // サーバ側で img に src が入った状態で描画されるので、connect でファイル名だけを
  // 書き直したいときに画像を消してはいけない。
  private updateSummaryText(text: string) {
    if (!this.summaryIdValue) return

    const summary = document.getElementById(this.summaryIdValue)
    if (summary) summary.textContent = text
  }

  private updateSummaryPreview(previewUrl?: string) {
    if (!this.summaryIdValue) return

    const preview = document.getElementById(`${this.summaryIdValue}-preview`)
    if (!(preview instanceof HTMLImageElement)) return

    if (previewUrl) {
      preview.src = previewUrl
      preview.classList.remove("hidden")
    } else {
      preview.removeAttribute("src")
      preview.classList.add("hidden")
    }
  }

  private revokePreviewUrl() {
    if (!this.previewUrl) return

    URL.revokeObjectURL(this.previewUrl)
    this.previewUrl = undefined
  }
}
